// Loading placeholders, styled after the support ticket skeleton: soft slate blocks that pulse
// together and stand still when the user prefers reduced motion. Only the blocks pulse; the
// cards, borders and shadows around them stay still, so nothing appears to lift or shrink.
// Blocks rendered together start their animation together, so they stay in sync.

const PULSE = "animate-pulse motion-reduce:animate-none";
// Layouts appear after a short delay (see index.css), so quick loads skip the placeholder.
const REVEAL = "animate-skeleton-in motion-reduce:animate-none";
// Line widths repeat in this order so stacked text never looks mechanical.
const LINE_WIDTHS = ["w-full", "w-[90%]", "w-3/5"];

// Two radius utilities on one element resolve by stylesheet order, not by the order written
// (rounded-md would beat rounded-full), so a shape's own radius replaces the default.
const withRadius = (className, radius) =>
  /(^|\s)rounded(-|\s|$)/.test(className) ? className : `${radius} ${className}`;


// A span, so a placeholder can also stand in for a value inside a paragraph.
export const Skeleton = ({ className = "" }) => (
  <span
    aria-hidden="true"
    className={`block bg-slate-200/70 dark:bg-slate-700 ${PULSE} ${withRadius(className, "rounded-md")}`}
  />
);

export const SkeletonText = ({ lines = 3, className = "h-4", widths = LINE_WIDTHS }) => (
  <div aria-hidden="true" className="flex flex-col gap-2">
    {Array.from({ length: lines }, (_, i) => (
      <Skeleton key={i} className={`${className} ${widths[i % widths.length]}`} />
    ))}
  </div>
);

export const SkeletonCircle = ({ className = "size-10" }) => (
  <Skeleton className={`shrink-0 ${withRadius(className, "rounded-full")}`} />
);

export const SkeletonButton = ({ className = "h-9 w-28" }) => (
  <Skeleton className={withRadius(className, "rounded-xl")} />
);

export const SkeletonBadge = ({ className = "h-5 w-16" }) => (
  <Skeleton className={withRadius(className, "rounded-full")} />
);

/** Announces the loading state once; use it where a SkeletonGroup cannot wrap the placeholders. */
export const SkeletonStatus = ({ label }) => (
  <span role="status" className="sr-only">
    {label}
  </span>
);

/** Wraps a skeleton layout, fades it in after a short delay and announces the loading state once. */
export const SkeletonGroup = ({ label, className = "", children }) => (
  <div aria-busy="true" className={`${REVEAL} ${className}`}>
    {label && <SkeletonStatus label={label} />}
    {children}
  </div>
);

/**
 * Placeholder rows for a table whose real header stays on screen. `columns` holds one
 * `{ className, render(rowIndex) }` per real column, so cell padding and alignment match.
 * Use the rows inside an existing <tbody>, or SkeletonTableBody in place of one.
 */
export const SkeletonTableRows = ({ rows = 5, columns }) =>
  Array.from({ length: rows }, (_, row) => (
    <tr key={row} aria-hidden="true" className={REVEAL}>
      {columns.map((column, i) => (
        <td key={i} className={column.className}>
          {column.render(row)}
        </td>
      ))}
    </tr>
  ));

export const SkeletonTableBody = ({ rows = 5, columns, className = "" }) => (
  <tbody aria-hidden="true" className={className}>
    <SkeletonTableRows rows={rows} columns={columns} />
  </tbody>
);

/** Picks a width from a list by row, so neighbouring rows differ. */
export const varyWidth = (row, widths = ["w-3/4", "w-1/2", "w-2/3", "w-5/6"]) =>
  widths[row % widths.length];

/** A number or short value still loading, sized to the surrounding text (1em tall). */
export const SkeletonValue = ({ className = "mx-auto w-12" }) => (
  <Skeleton className={`h-[1em] ${className}`} />
);

// Cells for the common table columns: one text-sized bar each, centred like the real cells.
// Kinds only set the bar width; badges, buttons and icons are left out to keep loading quiet.
const CELL_WIDTH = { seq: "w-6", number: "w-12", date: "w-20", pill: "w-16", button: "w-16" };

/** Columns for SkeletonTableRows from cell kinds, e.g. ["seq", "text", "pill", "date", "button"]. */
export const skeletonColumns = (kinds) =>
  kinds.map((kind, column) => ({
    render: (row) => (
      <Skeleton className={`mx-auto h-4 ${CELL_WIDTH[kind] || varyWidth(row + column)}`} />
    ),
  }));

/** An action-buttons column: kept empty while loading, so only content shows a placeholder. */
export const skeletonActions = () => ({ render: () => null });

const LABEL_WIDTHS = ["w-24", "w-32", "w-20", "w-28"];

/** Label-and-input placeholders to drop into a form's own grid. */
export const SkeletonFields = ({ count = 4, inputClassName = "h-10" }) =>
  Array.from({ length: count }, (_, i) => (
    <div key={i} aria-hidden="true">
      <Skeleton className={`mb-2 h-4 ${LABEL_WIDTHS[i % LABEL_WIDTHS.length]}`} />
      <Skeleton className={`w-full rounded-lg ${inputClassName}`} />
    </div>
  ));

const CELL_WIDTHS = ["w-3/4", "w-1/2", "w-2/3", "w-5/6"];

/** A bordered table placeholder (header row plus body rows) for places with no real table on screen yet. */
export const SkeletonTableFrame = ({ columns = 5, rows = 3, className = "" }) => (
  <div aria-hidden="true" className={`overflow-hidden rounded-xl border border-slate-200 dark:border-slate-700 ${className}`}>
    <div className="h-10 bg-slate-50 dark:bg-slate-800" />
    {Array.from({ length: rows }, (_, row) => (
      <div key={row} className="flex gap-6 border-t border-slate-200 px-3 py-3 dark:border-slate-700">
        {Array.from({ length: columns }, (_, i) => (
          <div key={i} className="flex-1">
            <Skeleton className={`h-4 ${CELL_WIDTHS[(row + i) % CELL_WIDTHS.length]}`} />
          </div>
        ))}
      </div>
    ))}
  </div>
);
